import go, { ObjectData } from 'gojs'
import { ReactDiagram } from 'gojs-react'
import { RefObject } from 'react'

import { DiagramState } from '@/pages/User/pages/Home/pages/DiagramDetail/types'
import {
	DiagramDataRead,
	ForeignKeyRead,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { StructureDtoRedux } from '@/store/modules/node'
import colors from '@/styles/diagramColors'
import { NativeMap } from '@/utils'

import {
	ParsedDiagramModel,
	ValidationError,
	ValidationErrorType,
} from './types'

interface DeletedTable {
	code: string
	id: string | number
	name: string
}

interface ModifiedForeignKey extends ForeignKeyRead {
	existingLink?: unknown // or whatever the actual type should be
}

interface DiagramChanges {
	deletedTables: DeletedTable[]
	foreignKeyChanges: {
		added: ForeignKeyRead[]
		modified: ModifiedForeignKey[]
		removed: ForeignKeyRead[]
	}
	hasChanges: boolean
	updatedTables: ObjectData[]
}

export const getNodePath = (
	allNodesFromTree: NativeMap<StructureDtoRedux>,
	nodeId: number | undefined,
): string => {
	const acc: string[] = []

	const buildPath = (currentId: number | undefined) => {
		const node = allNodesFromTree[currentId as number]
		if (!node) {
			return
		}
		if (node.parentStructureId) {
			buildPath(node.parentStructureId)
		}
		if (node.name !== undefined) {
			acc.push(node.name)
		}
	}

	buildPath(nodeId)
	return acc.join(' > ')
}

/**

Marks validation errors in the diagram. For nodes and links

related to the error, the 'changed' property is set to true.

@param diagram - The current instance of the diagram.

@param validationErrors - Array of validation errors.
*/
export const markChanges = (
	diagram: ReactDiagram | null,
	validationErrors: ValidationError[],
) => {
	if (diagram) {
		const instanceDiagram = diagram.getDiagram()
		const model = instanceDiagram?.model as go.GraphLinksModel

		if (model && instanceDiagram) {
			instanceDiagram.startTransaction('mark changes')

			validationErrors.forEach((error) => {
				const { type, nodeId } = error

				// Mark changed nodes
				const node = model.findNodeDataForKey(nodeId)
				if (
					node &&
					(type === ValidationErrorType.NODE_NOT_EXIST ||
						type === ValidationErrorType.TEXT_MISMATCH)
				) {
					model.setDataProperty(node, 'hasChanged', true)
					model.setDataProperty(node, 'bodyColor', colors.node.hasChanged)
				}

				// Mark changed links
				const link = model.findLinkDataForKey(nodeId)
				if (
					link &&
					(type === ValidationErrorType.NODE_NOT_EXIST ||
						type === ValidationErrorType.TEXT_MISMATCH ||
						type === ValidationErrorType.LINK_INVALID)
				) {
					model.setDataProperty(link, 'hasChanged', true)
				}
			})

			instanceDiagram.commitTransaction('mark changes')
		}
	}
}

export const serializeDiagramToSvg = (diagram: go.Diagram) => {
	const diagramSvg = diagram.makeSvg()
	return new XMLSerializer().serializeToString(diagramSvg as SVGAElement)
}

/**
 * Converts a GoJS diagram model to a JavaScript object
 * @param diagram - The GoJS diagram to convert
 * @returns The diagram model as a JavaScript object or undefined if conversion fails
 */

export const getModelAsObject = (diagram: go.Diagram): ParsedDiagramModel => {
	const modelAsJson = diagram?.model?.toJson()
	return JSON.parse(modelAsJson)
}

export const debugModelData = (diagramRef: RefObject<ReactDiagram> | null) => {
	const diagram = diagramRef?.current?.getDiagram()
	if (!diagram) {
		throw new Error('Diagram instance is null')
	}
	console.log('Model Data via useEffect:', diagram.model.modelData)
}

export const detectDiagramChanges = (
	backendData: DiagramDataRead,
	diagramState: DiagramState,
) => {
	const {
		tables: backendTables,
		notFoundTableIds,
		foreignKeys: backendFks,
	} = backendData || {}

	if (!backendTables && !notFoundTableIds?.length && !backendFks?.length) {
		return null
	}

	// Create maps for easier lookups
	const backendTablesMap = backendTables
		? new Map(backendTables.map((table) => [table.id, table]))
		: new Map()

	// Track different types of changes
	const changes: DiagramChanges = {
		deletedTables: [],
		updatedTables: [],
		foreignKeyChanges: {
			added: [],
			modified: [],
			removed: [],
		},
		hasChanges: false,
	}

	// Check for deleted tables
	if (notFoundTableIds?.length) {
		changes.deletedTables = diagramState.nodeDataArray
			?.filter((node) => notFoundTableIds.includes(Number(node.key)))
			.map((node) => ({
				id: node.key,
				name: node.text,
				code: node.code,
			}))

		if (changes.deletedTables?.length) {
			changes.hasChanges = true
		}
	}

	// Check for updated tables
	changes.updatedTables =
		diagramState.nodeDataArray?.flatMap((node) => {
			const nodeKey = Number(node.key)
			const backendTable = backendTablesMap.get(nodeKey)

			if (backendTable) {
				const currentVersion = node.version ? Number(node.version) : -1

				if (backendTable.version > currentVersion) {
					return [
						{
							id: nodeKey,
							name: node.text,
							newName: backendTable.name,
							code: node.code,
							newCode: backendTable.code,
							currentVersion,
							newVersion: backendTable.version,
						},
					]
				}
			}

			return [] // Return empty array instead of undefined
		}) || []

	if (changes.updatedTables?.length > 0) {
		changes.hasChanges = true
	}

	// Process foreign key changes
	if (backendFks?.length) {
		// Create a map of current links in the diagram using reduce instead of forEach
		const currentLinksMap = diagramState.linkDataArray?.reduce((map, link) => {
			const linkKey = `${link.from}-${link.to}-${link.text || ''}`
			map.set(linkKey, link)
			return map
		}, new Map())

		// Create a map of foreign keys from backend using reduce
		const backendFksMap = backendFks
			.filter((fk) => fk.source?.id && fk.target?.id) // Filter out invalid FKs first
			.reduce((map, fk) => {
				const fkKey = `${fk?.source?.id}-${fk?.target?.id}-${fk.name || ''}`
				map.set(fkKey, fk)
				return map
			}, new Map())

		// Find added/modified foreign keys using map and filter
		changes.foreignKeyChanges.added = backendFks
			.filter((fk) => fk.source?.id && fk.target?.id) // Only process valid FKs
			.map((fk) => {
				const fkKey = `${fk?.source?.id}-${fk?.target?.id}-${fk.name || ''}`
				const existingLink = currentLinksMap?.get(fkKey)

				if (!existingLink) {
					// This is a new foreign key
					// changes.hasChanges = true
					return {
						from: fk?.source?.id,
						to: fk?.target?.id,
						name: fk.name,
						code: fk.code,
						comment: fk.comment,
						columns: fk.columns,
					}
				}
				return fk
			})
			.filter(Boolean) // Remove null entries

		// Find modified foreign keys
		changes.foreignKeyChanges.modified = backendFks
			.filter((fk) => fk.source?.id && fk.target?.id) // Only process valid FKs
			.map((fk) => {
				const fkKey = `${fk?.source?.id}-${fk?.target?.id}-${fk.name || ''}`
				const existingLink = currentLinksMap?.get(fkKey)

				if (existingLink) {
					// Check if the existing link properties match the foreign key
					const isModified =
						existingLink.text !== fk.name ||
						existingLink.code !== fk.code ||
						existingLink.comment !== fk.comment

					if (isModified) {
						changes.hasChanges = true
						return {
							existingLink,
							newValues: {
								from: fk?.source?.id,
								to: fk?.target?.id,
								name: fk.name,
								code: fk.code,
								comment: fk.comment,
								columns: fk.columns,
							},
						}
					}
				}
				return fk
			})
			.filter(Boolean) // Remove null entries

		// Find removed foreign keys
		changes.foreignKeyChanges.removed = diagramState.linkDataArray
			?.map((link) => {
				const linkKey = `${link.from}-${link.to}-${link.text || ''}`

				if (!backendFksMap.has(linkKey)) {
					// This link exists in the diagram but not in the backend foreign keys
					// changes.hasChanges = true
					return {
						key: link.key,
						from: link.from,
						to: link.to,
						text: link.text,
					}
				}
				return link
			})
			.filter(Boolean) // Remove null entries
	}

	// Return null if no changes detected
	return changes.hasChanges ? changes : null
}
